import React, { FC, ReactNode, SyntheticEvent } from 'react';
import { Controller, ControllerRenderProps, FieldErrors, UseFormReturn } from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import { Autocomplete, AutocompleteRenderInputParams, TextField } from '@mui/material';
import ModbusWriteCoilScheduleEventCommand from './ModbusWriteCoilScheduleEventCommandConfig';
import { useTranslation } from 'react-i18next';
import {
  Device,
  DeviceModel,
  ModbusScheduleEventConfigType,
  ScheduleEventDeviceConfigType,
} from '@thingslog/repositories';

export const ModbusEvent: FC<ModbusEventProps> = ({ form }: ModbusEventProps) => {
  const {
    control,
    watch,

    register,
    formState: { errors: formErrors },
  } = form;
  const { t } = useTranslation();

  const eventType = watch('event.@type');
  const modbusSlaveDevices = watch('allDevices', []).filter(
    (device: Device) => device.model === DeviceModel.MODBUS_SLAVE
  );

  const errors = formErrors.event as FieldErrors<ModbusScheduleEventConfigType>;

  const renderModbusFields = (eventType: ScheduleEventDeviceConfigType): ReactNode => {
    switch (eventType) {
      case 'MODBUS_READ_COIL':
        return <></>;
      case 'MODBUS_READ_REGISTER':
        return <></>;
      case 'MODBUS_WRITE_COIL':
        return <ModbusWriteCoilScheduleEventCommand form={form} />;
      default:
        return null;
    }
  };

  return (
    <div className="col-span-4 grid grid-cols-1 gap-x-3 gap-y-4 md:grid-cols-4">
      {/* Slave Device Number */}
      <div className="col-span-4">
        <Controller
          control={control}
          name="event.slaveDeviceNumber"
          render={({
            field,
          }: {
            field: ControllerRenderProps<ScheduledEventForm, 'event.slaveDeviceNumber'>;
          }): JSX.Element => (
            <Autocomplete
              size="small"
              options={modbusSlaveDevices.map((device: Device) => device.number)}
              value={field.value || ''}
              onChange={(event: SyntheticEvent, value: string | null): void =>
                field.onChange(value)
              }
              renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                <TextField {...params} label={t('slave_device_number')} />
              )}
            />
          )}
        />
      </div>

      {/* Sensor Index */}
      <div className="col-span-2">
        <TextField
          label={t('sensor_index')}
          variant="outlined"
          type="number"
          {...register('event.sensorIndex', { valueAsNumber: true })}
          error={!!errors?.sensorIndex}
          helperText={errors?.sensorIndex?.message ?? ''}
          size="small"
          fullWidth
          inputProps={{ min: 0 }}
        />
      </div>

      {/* Modbus commandType-specific fields */}
      <div className="col-span-4 md:col-span-2">{renderModbusFields(eventType)}</div>
    </div>
  );
};

interface ModbusEventProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
}
